// useOfertasState.ts
import { useMotionValueEvent, useScroll } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { getFinanciacionType, getPagoFinanciado, getPagoUnico } from '../helpers/ofertas-helper';
import { handleModelListAutoScroll, scrollToCarline } from '../helpers/ofertas-scroll';
import { addCarlineInPath, cleanCarlineInPath, hasCarlineInPath } from '../helpers/ofertas-url-manager';
import { CarlineWithOfertas, Oferta } from '../services/ofertas-service/models/ofertas';
import { OfertasTrackingManager } from '../tracking/tracking-types';

export const useOfertasState = (carlines: CarlineWithOfertas[], trackingManager: OfertasTrackingManager) => {
	const [activeCarlineIndex, setActiveCarlineIndex] = useState<number>(-1);
	const [selectedOferta, setSelectedOferta] = useState<Oferta | undefined>();
	const [numberOfCarlines, setNumberOfCarlines] = useState<number>(0);
	const ofertasRef = useRef<HTMLDivElement | null>(null);
	const isAutoScrollingRef = useRef(false);
	const disableScrollDetection = useRef(false);

	const carlineBasicVehicleLoad = useRef(new Set());
	const carlineSelectedURLRef = React.useRef<boolean>(hasCarlineInPath(carlines));

	/* Control de scroll */
	const { scrollYProgress } = useScroll({
		target: ofertasRef,
		offset: ['start start', 'end end'],
	});

	//useEffect que se ejecuta cada vez que se cambia de coche
	useEffect(() => {
		if (activeCarlineIndex >= 0) {
			//Recalculamos margins de modelList
			handleModelListAutoScroll(activeCarlineIndex, numberOfCarlines);

			if (carlineSelectedURLRef.current) {
				addCarlineInPath(activeCarlineIndex, carlines);
				carlineSelectedURLRef.current = false;
			} else if (!disableScrollDetection.current) {
				cleanCarlineInPath();
			}

			//Scorll hasta carline seleccioando
			const viewportHeight = window.innerHeight;
			const rect = ofertasRef.current?.getBoundingClientRect();
			if (!rect) return;
			const scrollY = window.scrollY; // Obtiene el desplazamiento actual
			const ofertasRefTop = rect.top + scrollY;

			let scrollTo = ofertasRefTop;
			if (numberOfCarlines > 1) {
				const isLastCarline = activeCarlineIndex === numberOfCarlines - 1;
				scrollTo = isLastCarline
					? ofertasRefTop + viewportHeight * activeCarlineIndex
					: ofertasRefTop + viewportHeight * (activeCarlineIndex + 0.5);
			}

			// Validamos si el scrollYProgress concuerda con el activeCarlineIndex, en caso que no lo corregimos
			const currentScrollProgress = scrollYProgress.get();
			if (
				(1 / numberOfCarlines) * activeCarlineIndex < currentScrollProgress &&
				(1 / numberOfCarlines) * (activeCarlineIndex + 1) > currentScrollProgress
			) {
				return;
			} else {
				scrollToCarline(scrollTo, isAutoScrollingRef);
			}
		}
	}, [activeCarlineIndex, handleModelListAutoScroll, numberOfCarlines]);

	useMotionValueEvent(scrollYProgress, 'change', (value) => {
		if (!isAutoScrollingRef.current && !disableScrollDetection.current && numberOfCarlines > 0) {
			const carlineIndex = Math.min(Math.floor(value * numberOfCarlines), numberOfCarlines - 1);

			if (carlineIndex !== activeCarlineIndex && carlineIndex >= 0) {
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);

				if (!carlineBasicVehicleLoad.current.has(carlines[carlineIndex].code)) {
					const pagoFinanciado = getPagoFinanciado(carlines[carlineIndex].ofertas[0].datosEconomicos);

					trackingManager.trackOfertasVehicleLoad({
						CarlineId: carlines[carlineIndex].code.toString(),
						CarlineName: carlines[carlineIndex].title,
						SalesgroupName: carlines[carlineIndex].ofertas[0].carline?.salesgroup.title,
						ModelId: carlines[carlineIndex].ofertas[0].configuracion.motorizacion.code?.split('-')[0],
						ModelName: carlines[carlineIndex].ofertas[0].carline?.salesgroup.model.name,
						ModelYear: carlines[carlineIndex].ofertas[0].carline?.salesgroup.year.toString(),
						PriceRateType: pagoFinanciado ? getFinanciacionType(pagoFinanciado) : undefined,
						PriceRateTotal: pagoFinanciado?.precio ?? undefined,
						PriceRateCurrency: 'EUR',
						PriceTotalTotal: getPagoUnico(carlines[carlineIndex].ofertas[0].datosEconomicos)?.precio ?? undefined,
						PriceTotalCurrency: 'EUR',
					});
					carlineBasicVehicleLoad.current.add(carlines[carlineIndex].code);
				}
			}
		}
	});

	/* Reinicialización al cargar los carlines/CARGA INICIAL */
	useEffect(() => {
		if (carlines) {
			const prevNumberOfCarlines = numberOfCarlines;
			setNumberOfCarlines(carlines.length);

			const carlineUrlSplitted = window.location.pathname.split('/');
			const carlineUrl = carlineUrlSplitted[carlineUrlSplitted.length - 1].replace('.app', '');

			const carlineIndex = carlines
				.map(function (carline) {
					return carline.name;
				})
				.indexOf(carlineUrl);

			if (carlineIndex >= 0) {
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);

				if (hasCarlineInPath(carlines)) {
					//Desactivamos la detección de scroll temporalmente
					disableScrollDetection.current = true;
					setTimeout(() => {
						disableScrollDetection.current = false;
					}, 500);
				}
			} else {
				cleanCarlineInPath();
				setActiveCarlineIndex(0);
				setSelectedOferta(carlines[0].ofertas[0]);

				if (prevNumberOfCarlines !== carlines.length && window.innerWidth < 960) {
					setTimeout(() => {
						const rect = ofertasRef.current?.getBoundingClientRect();
						if (rect) {
							window.scrollTo({ top: rect.top + window.scrollY - 50 });
						}
					}, 100);
				}
			}
		}
	}, [carlines]);

	return {
		activeCarlineIndex,
		setActiveCarlineIndex,
		selectedOferta,
		setSelectedOferta,
		ofertasRef,
		isAutoScrollingRef,
		disableScrollDetection,
		trackingManager,
		numberOfCarlines,
		carlineSelectedURLRef,
	};
};
